import React, { useCallback, useEffect, useState } from 'react';
import BookingSummary from '../booking-summary/BookingSummary';
import Checkbox from '../checkbox/Checkbox';
import ParticipantSelector from '../participant-selector/ParticipantSelector';
import Switch from '../switch/Switch';
import Button from '../button/Button';
import Loading from '../loading/Loading';
import * as classes from './BookingWidget.module.css';
import { useCart, useCartDrawer, useProducts } from '../../context/shopContext';
import useBooking from '../../hooks/useBooking';
import useCalendar from '../../hooks/useCalendar';
import shortDates from '../../utils/shortDates';
import useEvents from '../../hooks/useEvents';
import useLocalisation from '../../hooks/useLocalisation';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS } from '@contentful/rich-text-types';
import * as selectClasses from '../../hooks/useCalendar.module.css';
import { set } from 'lodash';

const BookingWidget = ({ eventTypes, language, info }) => {
  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        return <p style={{ fontSize: '1rem' }}>{children}</p>;
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        return <h3>{children}</h3>;
      },
    },
  };

  const products = useProducts();

  const {
    initBooking,
    maxParticipants,
    bookingEvents,
    updateBookingEvents,
    participants,
    availableEvents,
    resolveAvailableEvents,
    loading,
    resolveSlots,
  } = useBooking(eventTypes);
  const { setCartDrawerOpen } = useCartDrawer();

  const { localize, toLocalisationKey } = useLocalisation(language);

  const { addToCart } = useCart();

  const { EventSelector, selectedEvent, clearEventData } = useEvents();

  const {
    week,
    setWeek,
    selectedWeek,
    WeekSelector,
    selectedDay,
    setSelectedDay,
  } = useCalendar();

  const [payInAdvance, setPayInAdvance] = useState(true);
  const [finished, setFinished] = useState(false);
  const [filteredWeek, setFilteredWeek] = useState([]);

  useEffect(() => {
    if (products) initBooking(products);
  }, [products]);

  // clear previous event data
  useEffect(() => {
    clearEventData();
  }, [selectedWeek, selectedDay]);

  // For resolving open events on week level..
  useEffect(() => {
    resolveAvailableEvents({ week });
  }, [selectedWeek]);

  const handleAddToCart = async (evt) => {
    evt.preventDefault();
    setFinished(true);

    const cartProducts = selectedEvent.reduce((acc, next) => {
      const product = products.find((p) =>
        p.metafield.eventType == 'custom'
          ? p.title === next.eventTitle
          : p.metafield.eventType === next.eventType
      );
      if (!product) return acc;

      const dateString = `${selectedDay.getDate()}.${
        selectedDay.getMonth() + 1
      }.${selectedDay.getFullYear()}`;
      const customAttributes = [
        {
          key: 'booking',
          value: JSON.stringify({
            time: `${dateString} ${next.slot}`,
            event: next.eventType,
          }),
        },
      ];

      const variantProduct = product.variants.edges.find(({ node }) =>
        payInAdvance
          ? parseFloat(node.price.amount) > 0
          : parseFloat(node.price.amount) == 0
      );
      return [
        ...acc,
        {
          variantId: variantProduct.node.id,
          quantity: participants[next.eventType.split('-')[0]][0],
          customAttributes,
        },
      ];
    }, []);

    addToCart(cartProducts);

    clearEventData();
    setSelectedDay(null);
    setFilteredWeek([]);
    updateBookingEvents(bookingEvents.map((b) => ({ ...b, selected: false })));
    clearSelectedDates();
  };

  const clearSelectedDates = () => {
    setWeek(week.map((w) => ({ ...w, selected: false })));
  };

  const handleEventChange = (index, state) => {
    const copy = [...bookingEvents];
    copy[index].selected = state;

    // When event is changed clear the state from previous selections except the week that shows up
    updateFilteredWeek();
    setSelectedDay(null);

    clearEventData();
    resolveAvailableEvents({
      event: copy[index],
      participantAmount: 1,
      week,
    });

    // When event is unchecked set the selected state for each date to false so e.g. Mon 26.6 is not selected anymore when selecting other events
    if (!state) clearSelectedDates();

    // If selected event does not have a presale variant, set pay in advance to true so customers can't try to add the non-existant variant to cart
    if (copy.find((e) => e.selected && !e.prepaidProductId)) {
      setPayInAdvance(true);
    }

    updateBookingEvents(copy);
    resolveSlots();
  };

  const handleDateChange = (state, w) => {
    let copy = [...week];
    if (!state) {
      setSelectedDay(null);
      clearSelectedDates();
      return;
    }

    setSelectedDay(w.date);
    const index = copy.findIndex((c) => c.date === w.date);
    if (~index) {
      copy = copy.map((c) => ({
        ...c,
        selected: false,
      }));
      copy[index].selected = state;
      setWeek(copy);
    }
  };

  const updateFilteredWeek = useCallback(() => {
    setFilteredWeek(week.filter((w) => availableEvents[w.date.getTime()]));
  }, [week, availableEvents]);

  const hasCheckedEvents = !!bookingEvents.find((e) => e.selected);

  useEffect(() => {
    updateFilteredWeek();
  }, [updateFilteredWeek]);

  const TourOrTasting = !!bookingEvents.find(
    (e) => e.selected & (e.value == 'tour' || e.value == 'tasting')
  );
  const customId =
    bookingEvents.find((e) => e.selected & (e.value == 'custom')) ?? false;

  return (
    <section>
      <form>
        <div className={classes.booking}>
          <div className={!finished ? classes.column : classes.thanks}>
            <h2>{localize('BOOKING_TITLE')}</h2>

            {info && renderRichText(info, options)}

            {!finished ? (
              <h4>{localize('EVENTS_TITLE')}</h4>
            ) : (
              <div>
                <h4>{localize('THANK_YOU')}</h4>
                <p>
                  {payInAdvance
                    ? localize('CHECKOUT_DESCRIPTION')
                    : localize('CHECKOUT_DESCRIPTION2')}
                </p>
                <Button
                  primary
                  onClick={() => setCartDrawerOpen(true)}
                  type="button"
                >
                  {localize('PROCEED_TO_CHECKOUT')}
                </Button>
                <Button
                  primary
                  onClick={() => setFinished(false)}
                  type="button"
                >
                  {localize('BACK_TO_SELECTION')}
                </Button>
              </div>
            )}
            {!finished ? (
              <div
                style={{
                  display: 'flex',
                  flexFlow: 'column',
                  margin: '0 0 2rem',
                  alignItems: 'flex-start',
                }}
              >
                {loading ? (
                  <Loading />
                ) : (
                  bookingEvents.map((event, i) => (
                    // Event type selector
                    <Checkbox
                      key={event.paidProductId}
                      id={event.value}
                      value={event.value}
                      name={
                        event.value == 'custom'
                          ? event.name
                          : localize(toLocalisationKey(event.name))
                      }
                      checked={event.selected}
                      disabled={
                        TourOrTasting & (event.value == 'custom')
                          ? true
                          : !customId
                          ? false
                          : customId.name == event.name
                          ? false
                          : true
                      }
                      onChange={(state) => {
                        handleEventChange(i, state);
                      }}
                    />
                  ))
                )}
              </div>
            ) : null}

            {!finished ? (
              <>
                <h4>{localize('PARTICIPANTS')}</h4>
                {hasCheckedEvents ? (
                  Object.keys(participants)
                    .filter((p) => {
                      const event = bookingEvents.find(
                        (e) => e.value === p && e.selected === true
                      );
                      return event ? true : false;
                    })
                    .sort((p) => (p === 'tour' ? -1 : 1))
                    .map((p) => {
                      const event = bookingEvents.find(
                        (e) => e.value === p && e.selected === true
                      );

                      var dropDownOptions = [];
                      for (let i = 1; i <= maxParticipants[p]; i++) {
                        dropDownOptions.push(
                          <option key={i} value={i}>
                            {i}
                          </option>
                        );
                      }
                      // Participant selector
                      return (
                        <div key={event.id}>
                          <h5>{event.name}</h5>
                          <div className={selectClasses.select}>
                            <select
                              value={participants[p][0]} // <- was giving the select element an array with the value inside it, not the value itself
                              onChange={(e) => {
                                clearEventData();
                                resolveAvailableEvents({
                                  event: p,
                                  participantAmount: [parseInt(e.target.value)],
                                  week,
                                });
                              }}
                            >
                              {dropDownOptions}
                            </select>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <p style={{ fontSize: '1rem' }}>
                    {localize('NO_SELECTED_EVENT')}
                  </p>
                )}

                {bookingEvents.find(
                  // If selected event does not have a prepaid variant, do not show 'Pay now' switch
                  (e) => e.selected && !e.prepaidProductId
                ) ? null : (
                  <>
                    <h4>{localize('PAY_NOW')}</h4>
                    <Switch checked={payInAdvance} onChange={setPayInAdvance} />
                  </>
                )}
              </>
            ) : null}
          </div>
          {!finished ? (
            <div className={classes.column} style={{ marginTop: 50 }}>
              {!!hasCheckedEvents && (
                <>
                  <h4>{localize('DATE_AND_TIME')}</h4>
                  <p style={{ fontSize: '1rem' }}>{localize('SELECT_WEEK')}</p>

                  <WeekSelector language={language} />
                  <p style={{ fontSize: '1rem' }}>
                    {localize('SELECT_AVAILABLE_DATE')}
                  </p>
                  <div className={classes.booking} style={{ padding: 0 }}>
                    <div className={classes.column}>
                      {!!week.length &&
                        filteredWeek.map((w) => {
                          const dateStr = `${localize(
                            toLocalisationKey(shortDates[w.date.getDay()])
                          )} ${w.date.getDate()}.${w.date.getMonth() + 1}.`;

                          // Date selector
                          return (
                            <Checkbox
                              key={w.date}
                              id={w.date}
                              value={w.date}
                              name={dateStr}
                              checked={w.selected}
                              disabled={w.disabled}
                              onChange={(state) => handleDateChange(state, w)}
                            />
                          );
                        })}
                      {!filteredWeek.length ? (
                        <p style={{ fontSize: '1rem' }}>
                          {localize('NO_OPEN_DAYS')}
                        </p>
                      ) : null}
                    </div>
                    <div className={classes.column}>
                      {selectedDay && availableEvents[selectedDay.getTime()] ? (
                        <EventSelector
                          events={availableEvents[selectedDay.getTime()]}
                          language={language}
                        />
                      ) : selectedDay ? (
                        <p style={{ fontSize: '1rem' }}>
                          {localize('NO_OPEN_SPOTS')}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
        <div className={classes.booking}>
          <div className={classes.column}>
            <BookingSummary
              paid={payInAdvance}
              language={language}
              items={bookingEvents
                .filter((item) => item.selected)
                .map((item) => {
                  return {
                    name: item.name,
                    value: item.value,
                    price: item.price,
                    amount: participants[item.value][0],
                    key: item.id,
                  };
                })}
            />
          </div>
          {!finished ? (
            <div className={classes.checkout}>
              <Button
                primary
                center
                onClick={handleAddToCart}
                disabled={!selectedEvent}
              >
                {payInAdvance
                  ? localize('BOOK_AND_PAY')
                  : localize('MAKE_A_RESERVATION')}
              </Button>
            </div>
          ) : null}
        </div>
      </form>
    </section>
  );
};

export default BookingWidget;
